import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

type MetricType = 'revenue' | 'orders' | 'roas';

export const BeforeAfterAnyActionChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, currencySettings, isLoading } = useBoardChartData();
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const [selectedMetric, setSelectedMetric] = useState<MetricType>('revenue');

    const chartData = useMemo(() => {
        if (!events || events.length === 0 || !finance?.dailyBreakdown) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        
        // Group events by category
        const categories: Record<string, { before: number; after: number; count: number }> = {};
        
        events.forEach((event: any) => {
            const eventDate = event.date?.split('T')[0];
            if (!eventDate) return;
            const dateIdx = sortedDates.indexOf(eventDate);
            if (dateIdx < 0) return;

            const windowSize = 7; // Use 7 days for more stability
            const beforeDays = sortedDates.slice(Math.max(0, dateIdx - windowSize), dateIdx);
            const afterDays = sortedDates.slice(dateIdx, Math.min(sortedDates.length, dateIdx + windowSize));

            if (beforeDays.length === 0 || afterDays.length === 0) return;

            const calculateValue = (days: string[]) => {
                const totals = days.reduce((acc, d) => {
                    const m = finance.dailyBreakdown![d]?.metrics;
                    acc.revenue += m?.revenue || 0;
                    acc.orders += m?.orders || 0;
                    acc.adSpend += m?.adSpend || 0;
                    return acc;
                }, { revenue: 0, orders: 0, adSpend: 0 });

                if (selectedMetric === 'revenue') return totals.revenue / days.length;
                if (selectedMetric === 'orders') return totals.orders / days.length;
                if (selectedMetric === 'roas') return totals.adSpend > 0 ? totals.revenue / totals.adSpend : 0;
                return 0;
            };

            const avgBefore = calculateValue(beforeDays);
            const avgAfter = calculateValue(afterDays);

            // Use eventType or a generic "Action"
            const cat = (event.type || event.eventType)?.split('_')[0] || 'other';
            if (!categories[cat]) categories[cat] = { before: 0, after: 0, count: 0 };
            
            categories[cat].before += avgBefore;
            categories[cat].after += avgAfter;
            categories[cat].count++;
        });

        const results = Object.entries(categories)
            .map(([name, data]) => ({
                name: name.charAt(0).toUpperCase() + name.slice(1),
                before: data.before / data.count,
                after: data.after / data.count,
                lift: data.before > 0 ? ((data.after / data.count - data.before / data.count) / (data.before / data.count) * 100) : 0
            }))
            .sort((a, b) => b.after - a.after)
            .slice(0, 5); // Show top 5 categories

        return results;
    }, [events, finance, selectedMetric]);

    const isEmpty = !isLoading && chartData.length === 0;
    
    const overallLift = useMemo(() => {
        if (chartData.length === 0) return 0;
        const totalBefore = chartData.reduce((s, d) => s + d.before, 0);
        const totalAfter = chartData.reduce((s, d) => s + d.after, 0);
        return totalBefore > 0 ? ((totalAfter - totalBefore) / totalBefore * 100) : 0;
    }, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span className={`text-[11px] font-bold ${overallLift >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {overallLift >= 0 ? '+' : ''}{overallLift.toFixed(1)}% {t('charts.components.averageLift')}
            </span>
        ) : undefined,
    });

    const metricFormatter = (v: number) => {
        if (selectedMetric === 'revenue') return formatCompactNumber(v, currencySymbol);
        if (selectedMetric === 'orders') return v.toFixed(1);
        if (selectedMetric === 'roas') return `${v.toFixed(2)}x`;
        return v.toString();
    };

    const tooltipFormatter = (v: number) => {
        if (selectedMetric === 'revenue') return formatCurrency(v, currencySymbol);
        if (selectedMetric === 'orders') return `${v.toFixed(2)} ${t('charts.components.orders')}`;
        if (selectedMetric === 'roas') return `${v.toFixed(2)}x ${t('charts.components.roas')}`;
        return v.toString();
    };

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="px-4 py-2 flex items-center justify-center gap-2 border-b border-gray-100/50">
                {[
                    { id: 'revenue', label: t('charts.components.metricRevenue') },
                    { id: 'orders', label: t('charts.components.metricOrders') },
                    { id: 'roas', label: t('charts.components.metricROAS') }
                ].map((m) => (
                    <button
                        key={m.id}
                        onClick={() => setSelectedMetric(m.id as any)}
                        className={`px-3 py-1 text-[10px] font-medium rounded-full transition-all ${
                            selectedMetric === m.id
                                ? 'bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20'
                                : 'text-gray-500 hover:bg-gray-100'
                        }`}
                    >
                        {m.label}
                    </button>
                ))}
            </div>

            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={CHART_STYLES.margin.withAxis} layout="vertical">
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} horizontal={true} vertical={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis type="number" {...CHART_STYLES.axis} tickFormatter={metricFormatter} />
                        <YAxis dataKey="name" type="category" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={70} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} formatter={tooltipFormatter} />
                        <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '5px' }} />
                        <Bar 
                            dataKey="before" 
                            name={t('charts.components.before')} 
                            fill={CHART_COLORS.gray[300]} 
                            radius={[0, 2, 2, 0]} 
                            barSize={12} 
                        />
                        <Bar 
                            dataKey="after" 
                            name={t('charts.components.after')} 
                            fill={CHART_COLORS.revenue} 
                            radius={[0, 2, 2, 0]} 
                            barSize={12} 
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
